import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { FINISH_LOOK, type FinishId } from '@goaltree/backend/convex/lib/dotFinishes'
import {
  DOT_PROMPT_MAX,
  type DotGlyph,
  type DotStyleInput,
  type DotView,
  EMOJI_PRESETS,
  SMILEY_DONE,
  doneGlyph,
  dotErrorCode,
  isSingleEmoji,
  missedLook,
  wantsFace,
} from '@goaltree/backend/convex/lib/habitDots'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Drawer, DrawerContent, DrawerTitle } from '@goaltree/ui/components/drawer'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { Camera, ChevronDown, ImagePlus, Loader2, Sparkles, Trash2, X } from 'lucide-react'
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { FinishRow, FinishSheen, finishFill } from '@/components/dot-finishes'
import { type ShadeDay, shadeMark } from '@/components/dot-shade'
import { habitIconComponent, useHabitIconName } from '@/components/habit-icon'
import { habitColor } from '@/components/inbox-habits'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { useIsMobile } from '@/hooks/use-mobile'
import { useCategoryFill } from '@/lib/category-color'

type HabitLike = { icon?: string | null; title: string; category?: string | null }

/** A done or missed cell's content: emoji, the habit icon in its colour, or a round image. */
export function DotGlyphView({
  glyph,
  size,
  color,
}: {
  glyph: DotGlyph
  size: number
  color?: string
}) {
  if (glyph.type === 'emoji')
    return (
      <span
        aria-hidden
        className="flex size-full select-none items-center justify-center leading-none"
        style={{ fontSize: Math.max(7, Math.round(size * 0.78)) }}
      >
        {glyph.text}
      </span>
    )
  if (glyph.type === 'image')
    return (
      <img
        src={glyph.url}
        alt=""
        draggable={false}
        className="block size-full rounded-full object-cover"
      />
    )
  const Icon = habitIconComponent(glyph.name)
  return (
    <span
      aria-hidden
      className="flex size-full items-center justify-center text-[var(--habit)]"
      style={color ? { color } : undefined}
    >
      <Icon style={{ width: size * 0.68, height: size * 0.68 }} strokeWidth={2.6} />
    </span>
  )
}

const TINT = 'color-mix(in srgb, var(--habit) 18%, transparent)'
const RING = 'inset 0 0 0 1.5px color-mix(in srgb, var(--habit) 34%, transparent)'

export type CellMark = { style: CSSProperties; children: ReactNode }

/**
 * Cell overrides for a habit's dot style: `done(size)` for a done day,
 * `missed(size)` for a scheduled day that was missed. Null = the usual look.
 */
export function useDotMarks(
  view: DotView | null | undefined,
  habit: HabitLike,
  finish?: FinishId | null,
) {
  const icon = useHabitIconName(habit)
  const v = view ?? null
  return {
    view: v,
    finish: finish ?? null,
    // `i` (position along the row) turns on a finish's sweep for this cell.
    done(size: number, i?: number): CellMark | null {
      const g = doneGlyph(v, size, { ...habit, icon })
      if (finish)
        return {
          style: finishFill(finish),
          children: (
            <>
              {g && (
                <span
                  className="relative flex size-full items-center justify-center"
                  style={{ padding: Math.max(1.5, size * 0.12) }}
                >
                  <DotGlyphView glyph={g} size={size * 0.76} color={FINISH_LOOK[finish].ink} />
                </span>
              )}
              <FinishSheen finish={finish} size={size} i={i} />
            </>
          ),
        }
      if (!g) return null
      return {
        style: { backgroundColor: g.type === 'icon' ? TINT : 'transparent' },
        children: <DotGlyphView glyph={g} size={size} />,
      }
    },
    missed(size: number): CellMark | null {
      const look = missedLook(v, size)
      if (look === 'plain') return null
      if (look === 'ring')
        return { style: { backgroundColor: 'transparent', boxShadow: RING }, children: null }
      return {
        style: { backgroundColor: 'transparent', backgroundImage: 'none' },
        children: <DotGlyphView glyph={look} size={size} />,
      }
    },
  }
}

/** The habit's dot view from the (already subscribed) habits list. */
export function useHabitDotView(habitId: string | undefined) {
  const habits = useQuery(api.nodes.getHabits, habitId ? {} : 'skip')
  const h = habitId ? habits?.find((x) => x._id === habitId) : undefined
  return { view: h?.dot ?? null, finish: h?.finish ?? null, habit: h }
}

// ─── Picker ──────────────────────────────────────────────────────────────────

type Kind = DotStyleInput['kind']
const PREVIEW = [1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 1, 0, 1, 1]
const PREVIEW_PX = 20

// Sample day credits for the shaded preview (>1 = over target).
const PREVIEW_CREDITS = [1, 0.75, 0, 1, 0.5, 1.25, 0, 0.9, 1, 0.4, 1, 0, 0.7, 1]
const previewShade = (c: number): ShadeDay | undefined =>
  c > 0 ? { credit: Math.min(1, c), over: c > 1, amount: c, target: 1 } : undefined

function PreviewStrip({
  view,
  habit,
  finish,
  shade,
}: {
  view: DotView
  habit: HabitLike
  finish: FinishId | null
  shade?: boolean
}) {
  const marks = useDotMarks(view, habit, finish)
  // AI image picked but none generated yet: an empty slot, not the icon fallback.
  const awaiting = view?.kind === 'image' && !view.url
  return (
    <div className="flex gap-1">
      {PREVIEW.map((done, i) => {
        const today = i === PREVIEW.length - 1
        const base =
          done && awaiting
            ? {
                style: {
                  backgroundColor: 'transparent',
                  border: '1.5px dashed var(--habit)',
                },
                children: null,
              }
            : done
              ? marks.done(PREVIEW_PX, i)
              : marks.missed(PREVIEW_PX)
        const mark = shade ? shadeMark(base, previewShade(PREVIEW_CREDITS[i])) : base
        return (
          <span
            key={i}
            className={cn(
              'block shrink-0 overflow-hidden rounded-[5px]',
              !done && 'bg-[color-mix(in_srgb,var(--habit)_16%,transparent)]',
              today && 'ring-[1.5px] ring-[var(--habit)]',
            )}
            style={{
              width: PREVIEW_PX,
              height: PREVIEW_PX,
              ...(done ? { backgroundColor: 'var(--habit)' } : {}),
              ...mark?.style,
            }}
          >
            {mark?.children}
          </span>
        )
      })}
    </div>
  )
}

function errorText(t: (k: string) => string, err: unknown) {
  const code = dotErrorCode(err)
  return code ? t(`dotStyle.errors.${code}`) : t('dotStyle.errors.generationFailed')
}

async function squareJpeg(file: File, size = 448): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = reject
      el.src = url
    })
    const side = Math.min(img.naturalWidth, img.naturalHeight)
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = size
    canvas
      .getContext('2d')!
      .drawImage(
        img,
        (img.naturalWidth - side) / 2,
        (img.naturalHeight - side) / 2,
        side,
        side,
        0,
        0,
        size,
        size,
      )
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/jpeg', 0.85),
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

type Picker = NonNullable<FunctionReturnType<typeof api.habitDots.dotPicker>>

function SelfieRow({ picker }: { picker: Picker }) {
  const { t } = useTranslation()
  const uploadUrl = useMutation(api.habitDots.selfieUploadUrl)
  const setSelfie = useMutation(api.habitDots.setDotSelfie)
  const deleteSelfie = useMutation(api.habitDots.deleteDotSelfie)
  const importAvatar = useAction(api.habitDotsNode.importAvatarSelfie)
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const triedAvatar = useRef(false)
  const hasSelfie = !!picker.selfieUrl
  useEffect(() => {
    if (hasSelfie || triedAvatar.current) return
    triedAvatar.current = true
    setBusy(true)
    importAvatar({})
      .catch(() => {})
      .finally(() => setBusy(false))
  }, [hasSelfie, importAvatar])
  const upload = async (file: File) => {
    setBusy(true)
    try {
      const body = await squareJpeg(file)
      const res = await fetch(await uploadUrl({}), {
        method: 'POST',
        headers: { 'Content-Type': 'image/jpeg' },
        body,
      })
      if (!res.ok) throw new Error('upload failed')
      const { storageId } = (await res.json()) as { storageId: Id<'_storage'> }
      const out = await setSelfie({ storageId })
      if (out !== 'ok') toast.error(t(`dotStyle.errors.${out}`))
    } catch (err) {
      toast.error(dotErrorCode(err) ? errorText(t, err) : t('dotStyle.errors.badImage'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        onClick={() => input.current?.click()}
        aria-label={t('dotStyle.addSelfie')}
        className="flex size-11 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted/70"
      >
        {busy ? (
          <Loader2 className="size-4 animate-spin" />
        ) : picker.selfieUrl ? (
          <img src={picker.selfieUrl} alt="" className="size-full object-cover" />
        ) : (
          <ImagePlus className="size-4" />
        )}
      </button>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-xs font-semibold">
          {picker.selfieUrl
            ? picker.selfieFromAvatar
              ? t('dotStyle.selfieFromAvatar')
              : t('dotStyle.selfieSaved')
            : t('dotStyle.addSelfie')}
        </span>
        <span className="text-[11px] text-muted-foreground">{t('dotStyle.faceNote')}</span>
      </div>
      {picker.selfieUrl && (
        <button
          type="button"
          onClick={() => void deleteSelfie({})}
          aria-label={t('dotStyle.deleteSelfie')}
          title={t('dotStyle.deleteSelfie')}
          className="flex size-9 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Trash2 className="size-4" />
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) void upload(f)
        }}
      />
    </div>
  )
}

// Prompt → (optional selfie) → Generate, which applies at once; Undo restores
// the image it replaced while the picker is open.
function ImageMaker({
  habitId,
  picker,
  slot,
  currentUrl,
  onUndo,
}: {
  habitId: Id<'nodes'>
  picker: Picker
  slot: 'done' | 'missed'
  currentUrl: string | null
  onUndo: (img: { storageId: Id<'_storage'>; prompt: string }) => Promise<void>
}) {
  const { t } = useTranslation()
  const generate = useAction(api.habitDotsNode.generateDotImage)
  const applyLibrary = useMutation(api.habitDots.applyLibraryImage)
  const removeLibrary = useMutation(api.habitDots.removeLibraryImage)
  const previous = picker.previous[slot]
  const savedPrompt = slot === 'done' ? picker.savedPrompt : picker.savedMissedPrompt
  const [prompt, setPrompt] = useState(savedPrompt ?? '')
  const [face, setFace] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [applied, setApplied] = useState(false)
  const useFace = picker.faceImages && (face ?? wantsFace(prompt))
  const suggestions =
    slot === 'done'
      ? [t('dotStyle.suggestions.0'), t('dotStyle.suggestions.1'), t('dotStyle.suggestions.2')]
      : [t('dotStyle.missedSuggestions.0'), t('dotStyle.missedSuggestions.1')]
  const run = async () => {
    setBusy(true)
    setApplied(false)
    try {
      await generate({
        habitId,
        prompt,
        face: useFace,
        slot,
        tzOffsetMin: new Date().getTimezoneOffset(),
      })
      setApplied(true)
    } catch (err) {
      toast.error(errorText(t, err))
    } finally {
      setBusy(false)
    }
  }
  const canGenerate =
    prompt.trim().length >= 2 && !busy && picker.remaining > 0 && (!useFace || !!picker.selfieUrl)
  return (
    <div className="flex flex-col gap-2.5">
      <label htmlFor={`dot-prompt-${slot}`} className="text-xs font-bold text-foreground/80">
        {slot === 'done' ? t('dotStyle.promptLabel') : t('dotStyle.missedTitle')}
      </label>
      <textarea
        id={`dot-prompt-${slot}`}
        value={prompt}
        maxLength={DOT_PROMPT_MAX}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={
          slot === 'done' ? t('dotStyle.promptPlaceholder') : t('dotStyle.missedPromptPlaceholder')
        }
        rows={2}
        className="min-h-14 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      />
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setPrompt(s)}
            className="cursor-pointer rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            {s}
          </button>
        ))}
      </div>
      {picker.faceImages ? (
        <>
          <label className="flex cursor-pointer items-center justify-between gap-3 text-xs font-semibold">
            {t('dotStyle.useFace')}
            <input
              type="checkbox"
              role="switch"
              checked={useFace}
              onChange={(e) => setFace(e.target.checked)}
              className="peer sr-only"
            />
            <span
              aria-hidden
              className="relative h-6 w-10 shrink-0 rounded-full bg-muted transition-colors peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring/50 after:absolute after:top-[3px] after:left-[3px] after:size-[18px] after:rounded-full after:bg-background after:transition-transform peer-checked:after:translate-x-4"
            />
          </label>
          {useFace && <SelfieRow picker={picker} />}
        </>
      ) : (
        <p className="text-[11px] text-muted-foreground">{t('dotStyle.faceSoon')}</p>
      )}
      <div className="flex items-center gap-3">
        <div className="flex size-[72px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
          {busy ? (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          ) : currentUrl ? (
            <img src={currentUrl} alt="" className="size-full object-cover" />
          ) : (
            <Sparkles className="size-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <button
            type="button"
            disabled={!canGenerate}
            onClick={() => void run()}
            className="h-10 cursor-pointer rounded-xl bg-primary text-sm font-extrabold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy
              ? t('dotStyle.generating')
              : currentUrl
                ? t('dotStyle.regenerate', { count: picker.remaining })
                : t('dotStyle.generate', { count: picker.remaining })}
          </button>
          {previous && !busy && (
            <button
              type="button"
              disabled={saving}
              onClick={async () => {
                setSaving(true)
                try {
                  await onUndo({ storageId: previous.storageId, prompt: previous.prompt })
                  setApplied(false)
                } finally {
                  setSaving(false)
                }
              }}
              className="h-9 cursor-pointer rounded-xl border border-border text-xs font-semibold transition-colors hover:bg-muted disabled:opacity-60"
            >
              {t('dotStyle.undo')}
            </button>
          )}
        </div>
      </div>
      {applied && !busy && (
        <p className="text-[11px] text-muted-foreground">{t('dotStyle.applied')}</p>
      )}
      {picker.library.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-border pt-2.5">
          <span className="text-xs font-bold text-foreground/80">{t('dotStyle.recent')}</span>
          <span className="text-[11px] text-muted-foreground">{t('dotStyle.recentHint')}</span>
          <div className="flex gap-2 overflow-x-auto py-1">
            {picker.library.map((img) => (
              <div key={img.id} className="relative shrink-0">
                <button
                  type="button"
                  disabled={busy}
                  title={img.prompt}
                  aria-label={img.prompt}
                  onClick={() =>
                    void applyLibrary({ habitId, slot, libraryId: img.id }).then(
                      () => {
                        setApplied(false)
                        toast.success(t('dotStyle.recentApplied'))
                      },
                      (err) => toast.error(errorText(t, err)),
                    )
                  }
                  className="block size-12 cursor-pointer overflow-hidden rounded-full bg-muted transition-opacity hover:opacity-80 disabled:opacity-50"
                >
                  <img src={img.url} alt="" className="size-full object-cover" />
                </button>
                <button
                  type="button"
                  aria-label={t('dotStyle.recentRemove')}
                  title={t('dotStyle.recentRemove')}
                  onClick={() =>
                    void removeLibrary({ libraryId: img.id }).catch((err) =>
                      toast.error(errorText(t, err)),
                    )
                  }
                  className="absolute -top-1 -right-1 flex size-5 cursor-pointer items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {picker.remaining <= 0 && (
        <p className="text-[11px] text-muted-foreground">{t('dotStyle.errors.dailyCap')}</p>
      )}
    </div>
  )
}

function PickerBody({
  habitId,
  habit,
  picker,
  finish: savedFinish,
}: {
  habitId: Id<'nodes'>
  habit: HabitLike
  picker: Picker
  finish: FinishId | null
}) {
  const { t } = useTranslation()
  const pro = picker.pro
  const [upgrade, setUpgrade] = useState(false)
  const [finish, setFinish] = useState(savedFinish)
  useEffect(() => setFinish(savedFinish), [savedFinish])
  const save = useMutation(api.habitDots.setDotStyle)
  const setDotShade = useMutation(api.habitPhotos.setDotShade)
  const setAsk = useMutation(api.habitPhotos.setPhotoAsk)
  const [shade, setShade] = useState(picker.shade)
  const [photoAsk, setPhotoAsk] = useState(picker.photoAsk)
  const discard = useMutation(api.habitDots.discardDotImages)
  const saved = picker.saved
  const [kind, setKind] = useState<Kind>(saved?.kind ?? 'default')
  const [emojiText, setEmojiText] = useState(saved?.kind === 'emoji' ? saved.emoji : '')
  const [missedOpen, setMissedOpen] = useState(!!picker.savedIds.missed)
  const savedKind = saved?.kind
  useEffect(() => {
    if (savedKind) setKind(savedKind)
  }, [savedKind])
  const iconName = useHabitIconName(habit)
  const Icon = habitIconComponent(iconName)

  const apply = async (style: DotStyleInput) => {
    try {
      await save({ habitId, style })
    } catch (err) {
      toast.error(errorText(t, err))
    }
  }
  const emoji = saved?.kind === 'emoji' ? saved.emoji : '💪'
  const options: { kind: Kind; label: string; glyph: ReactNode }[] = [
    {
      kind: 'default',
      label: t('dotStyle.options.default'),
      glyph: <span className="size-4 rounded-[4px] bg-[var(--habit)]" />,
    },
    { kind: 'smiley', label: t('dotStyle.options.smiley'), glyph: SMILEY_DONE },
    { kind: 'emoji', label: t('dotStyle.options.emoji'), glyph: emoji },
    {
      kind: 'icon',
      label: t('dotStyle.options.icon'),
      glyph: <Icon className="size-5 text-[var(--habit)]" strokeWidth={2.4} />,
    },
    {
      kind: 'photo',
      label: t('dotStyle.options.photo'),
      glyph: <Camera className="size-5 text-[var(--habit)]" strokeWidth={2.2} />,
    },
    {
      kind: 'image',
      label: t('dotStyle.options.image'),
      glyph:
        saved?.kind === 'image' && saved.url ? (
          <img src={saved.url} alt="" className="size-6 rounded-full object-cover" />
        ) : (
          <Sparkles className="size-5 text-[var(--habit)]" />
        ),
    },
  ]
  const pick = (k: Kind) => {
    if (!pro && k !== 'default' && k !== 'photo') return setUpgrade(true)
    setKind(k)
    if (k === 'default' || k === 'smiley' || k === 'icon' || k === 'photo') void apply({ kind: k })
    if (k === 'emoji' && saved?.kind !== 'emoji') void apply({ kind: 'emoji', emoji })
  }
  const pickEmoji = (e: string) => {
    setEmojiText(e)
    if (isSingleEmoji(e.trim())) void apply({ kind: 'emoji', emoji: e.trim() })
  }

  const savedImage = saved?.kind === 'image' ? saved : null
  const shownKind = pro || kind === 'photo' ? kind : 'default'
  const previewView: DotView =
    shownKind === 'default'
      ? null
      : kind === 'photo'
        ? { kind: 'photo' }
        : kind === 'smiley' || kind === 'icon'
          ? { kind }
          : kind === 'emoji'
            ? {
                kind: 'emoji',
                emoji: isSingleEmoji(emojiText.trim()) ? emojiText.trim() : emoji,
              }
            : {
                kind: 'image',
                url: savedImage?.url ?? null,
                missedUrl: savedImage?.missedUrl ?? null,
              }

  const undoDone = async (img: { storageId: Id<'_storage'>; prompt: string }) => {
    const missed =
      picker.savedIds.missed && picker.savedMissedPrompt
        ? { storageId: picker.savedIds.missed, prompt: picker.savedMissedPrompt }
        : undefined
    await apply({ kind: 'image', ...img, ...(missed && { missedImage: missed }) })
  }
  const undoMissed = async (img: { storageId: Id<'_storage'>; prompt: string }) => {
    if (!picker.savedIds.done || !picker.savedPrompt) return
    await apply({
      kind: 'image',
      storageId: picker.savedIds.done,
      prompt: picker.savedPrompt,
      missedImage: img,
    })
  }

  useEffect(() => () => void discard({ habitId }).catch(() => {}), [discard, habitId])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground uppercase">
          {t('dotFinish.styleSection')}
        </span>
        {!pro && (
          <span className="rounded-full bg-primary/15 px-1.5 py-0.5 font-mono text-[9px] font-bold text-primary-text">
            {t('dotStyle.pro')}
          </span>
        )}
      </div>
      <div className="-mt-2 grid grid-cols-3 gap-1.5 sm:grid-cols-6">
        {options.map((o) => (
          <button
            key={o.kind}
            type="button"
            aria-pressed={shownKind === o.kind}
            onClick={() => pick(o.kind)}
            className={cn(
              'flex h-[70px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border bg-muted/40 px-1 text-[11px] font-semibold transition-colors hover:bg-muted',
              shownKind === o.kind ? 'border-2 border-primary' : 'border-border',
              !pro && o.kind !== 'default' && o.kind !== 'photo' && 'opacity-60',
            )}
          >
            <span className="flex h-6 items-center justify-center text-xl leading-none">
              {o.glyph}
            </span>
            <span className="max-w-full truncate">{o.label}</span>
          </button>
        ))}
      </div>

      <FinishRow habitId={habitId} value={finish} onChange={setFinish} />

      <div className="flex flex-col gap-1.5">
        <span className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground uppercase">
          {t('dotStyle.preview')}
        </span>
        <PreviewStrip
          view={previewView}
          habit={habit}
          finish={finish}
          shade={picker.numberHabit && shade}
        />
      </div>

      {picker.numberHabit && (
        <label className="flex cursor-pointer items-center justify-between gap-3">
          <span className="flex flex-col">
            <span className="text-xs font-bold">{t('dotShade.toggle')}</span>
            <span className="text-[11px] text-muted-foreground">{t('dotShade.toggleHint')}</span>
          </span>
          <input
            type="checkbox"
            role="switch"
            checked={shade}
            onChange={(e) => {
              setShade(e.target.checked)
              void setDotShade({ habitId, shade: e.target.checked })
            }}
            className="peer sr-only"
          />
          <span
            aria-hidden
            className="relative h-6 w-10 shrink-0 rounded-full bg-muted transition-colors peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring/50 after:absolute after:top-[3px] after:left-[3px] after:size-[18px] after:rounded-full after:bg-background after:transition-transform peer-checked:after:translate-x-4"
          />
        </label>
      )}

      {kind === 'photo' && (
        <div className="flex flex-col gap-2 rounded-2xl border border-border bg-muted/30 p-3">
          <p className="text-[11px] text-muted-foreground">{t('dotPhoto.pickerHint')}</p>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold">{t('dotPhoto.askLabel')}</span>
            <div className="inline-flex rounded-lg border border-border/60 p-0.5">
              {(['always', 'sometimes', 'never'] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={photoAsk === a}
                  onClick={() => {
                    setPhotoAsk(a)
                    void setAsk({ habitId, ask: a })
                  }}
                  className={cn(
                    'cursor-pointer rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                    photoAsk === a
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`dotPhoto.ask.${a}`)}
                </button>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{t('dotPhoto.askHint')}</p>
        </div>
      )}

      <UpgradeDialog
        open={upgrade}
        onOpenChange={setUpgrade}
        reason="pro"
        title={t('dotStyle.upgradeTitle')}
        description={t('dotStyle.upgradeDesc')}
      />

      {pro && kind === 'emoji' && (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-6 gap-1.5">
            {EMOJI_PRESETS.map((e) => (
              <button
                key={e}
                type="button"
                aria-pressed={emojiText.trim() === e}
                onClick={() => pickEmoji(e)}
                className={cn(
                  'flex aspect-square cursor-pointer items-center justify-center rounded-xl text-xl transition-colors hover:bg-muted',
                  (emojiText.trim() || emoji) === e && 'bg-muted ring-1 ring-primary',
                )}
              >
                {e}
              </button>
            ))}
          </div>
          <input
            value={emojiText}
            onChange={(e) => pickEmoji(e.target.value)}
            placeholder={t('dotStyle.emojiPlaceholder')}
            aria-label={t('dotStyle.emojiPlaceholder')}
            className="h-10 rounded-xl border border-border bg-background px-3 text-base outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          />
          {emojiText.trim() !== '' && !isSingleEmoji(emojiText.trim()) && (
            <p className="text-[11px] text-destructive">{t('dotStyle.errors.badEmoji')}</p>
          )}
        </div>
      )}

      {pro && kind === 'image' && (
        <div className="flex flex-col gap-3 rounded-2xl border border-border bg-muted/30 p-3">
          <ImageMaker
            habitId={habitId}
            picker={picker}
            slot="done"
            currentUrl={savedImage?.url ?? null}
            onUndo={undoDone}
          />
          {picker.savedIds.done && (
            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <button
                type="button"
                aria-expanded={missedOpen}
                onClick={() => setMissedOpen((o) => !o)}
                className="flex cursor-pointer items-center justify-between text-left text-xs font-semibold"
              >
                {picker.savedIds.missed ? t('dotStyle.missedTitle') : t('dotStyle.missedAdd')}
                <ChevronDown
                  className={cn('size-4 transition-transform', missedOpen && 'rotate-180')}
                />
              </button>
              {missedOpen && (
                <>
                  <p className="text-[11px] text-muted-foreground">{t('dotStyle.missedHint')}</p>
                  <ImageMaker
                    habitId={habitId}
                    picker={picker}
                    slot="missed"
                    currentUrl={savedImage?.missedUrl ?? null}
                    onUndo={undoMissed}
                  />
                  {picker.savedIds.missed && picker.savedPrompt && (
                    <button
                      type="button"
                      onClick={() =>
                        void apply({
                          kind: 'image',
                          storageId: picker.savedIds.done!,
                          prompt: picker.savedPrompt!,
                        })
                      }
                      className="cursor-pointer self-start text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    >
                      {t('dotStyle.missedRemove')}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function HabitDotPicker({
  open,
  onOpenChange,
  habitId,
  habit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  habitId: Id<'nodes'>
  habit: HabitLike
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const fill = useCategoryFill()
  const picker = useQuery(api.habitDots.dotPicker, open ? { habitId } : 'skip')
  const { finish } = useHabitDotView(open ? habitId : undefined)
  const Title = isMobile ? DrawerTitle : DialogTitle
  const body = (
    <div
      className={cn('flex flex-col gap-4 overflow-y-auto pb-6', isMobile ? 'px-5 pt-2' : 'px-1')}
      style={{ '--habit': fill(habitColor(habit.category ?? undefined)) } as CSSProperties}
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Title className="text-xl font-black tracking-tight">{t('dotStyle.title')}</Title>
        </div>
        <p className="truncate text-[13px] text-muted-foreground">
          {t('dotStyle.subtitle', { title: habit.title.split('///')[0].trim() })}
        </p>
      </div>
      {picker ? (
        <PickerBody habitId={habitId} habit={habit} picker={picker} finish={finish} />
      ) : (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  )
  return isMobile ? (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto w-full max-w-lg overflow-hidden data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=bottom]:rounded-t-3xl">
        {body}
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-md overflow-y-auto">{body}</DialogContent>
    </Dialog>
  )
}

/** The habit sheet's "Dot style" value: the current look, or a Pro badge on Free. */
export function DotStyleButton({ habitId, habit }: { habitId: Id<'nodes'>; habit: HabitLike }) {
  const { t } = useTranslation()
  const { isPro } = useIsPro()
  const { view, finish } = useHabitDotView(habitId)
  const fill = useCategoryFill()
  const marks = useDotMarks(view, habit, finish)
  const [open, setOpen] = useState(false)
  const style = view ? t(`dotStyle.options.${view.kind}`) : t('dotStyle.options.default')
  const label = finish ? `${style} · ${t(`dotFinish.names.${finish}`)}` : style
  const sample = marks.done(16)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="-mx-1 flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50"
        style={{ '--habit': fill(habitColor(habit.category ?? undefined)) } as CSSProperties}
      >
        <span
          aria-hidden
          className="relative block size-4 overflow-hidden rounded-[4px]"
          style={{ backgroundColor: 'var(--habit)', ...sample?.style }}
        >
          {sample?.children}
        </span>
        <span>{label}</span>
        {!isPro && (
          <span className="rounded-full bg-primary/15 px-1.5 py-0.5 font-mono text-[9px] font-bold text-primary-text">
            {t('dotStyle.pro')}
          </span>
        )}
      </button>
      <HabitDotPicker open={open} onOpenChange={setOpen} habitId={habitId} habit={habit} />
    </>
  )
}
